import React, { useState, VFC } from 'react';

import { AxiosInstance } from 'axios';
import { noop } from 'lodash';
import { createBridgeComponent } from 'mfe-react-bridge/react17';
import { Button, Text } from 'mintel-react-ui';

import {
  CustomPanelType,
  HandlePresentType,
  TrackPresentPanelDownloadFn,
} from '/types';
import useTranslate, { Trans } from '/utils/translations';

import styles from './PresentPanel.scss';
import fileDownload from '../fileDownload';

type PresentPanelSectionProps = {
  header: string;
  body: string;
  enablePresent: () => void;
  text: string;
  cwaAxiosTaskbar: AxiosInstance;
  downloadPath?: string;
  trackPresentPanelDownload: TrackPresentPanelDownloadFn;
};

const PresentPanelSection: VFC<PresentPanelSectionProps> = ({
  header,
  body,
  enablePresent,
  text,
  cwaAxiosTaskbar,
  downloadPath,
  trackPresentPanelDownload,
}) => {
  const [buttonStatus, setButtonStatus] = useState(Button.Status.DEFAULT);
  return (
    <div>
      <Text.Heading5 darkBg tagName="h5">
        {header}
      </Text.Heading5>
      <Text.Body darkBg tagName="p">
        {body}
      </Text.Body>
      <div className={styles.buttons}>
        <div className={styles.button}>
          <Button.ActionButton handleClick={enablePresent} fullWidth>
            {text}
          </Button.ActionButton>
        </div>
        {downloadPath && (
          <div className={styles.button}>
            <Button.DownloadButton
              handleClick={(): Promise<void> => {
                setButtonStatus(Button.Status.LOADING);
                trackPresentPanelDownload();
                return fileDownload(cwaAxiosTaskbar, downloadPath)
                  .then(() => setButtonStatus(Button.Status.SUCCESS))
                  .catch(() => setButtonStatus(Button.Status.ERROR));
              }}
              status={buttonStatus}
              fullWidth
            />
          </div>
        )}
      </div>
    </div>
  );
};

const PresentPanelComponent: VFC<{
  handlePresent: HandlePresentType;
  cwaAxiosTaskbar: AxiosInstance;
  downloadPath?: string;
  trackPresentPanelDownload: TrackPresentPanelDownloadFn;
}> = ({
  handlePresent,
  cwaAxiosTaskbar,
  downloadPath,
  trackPresentPanelDownload,
}) => {
  const t = useTranslate();
  return (
    <>
      <PresentPanelSection
        header={t('Keynote')}
        body={t(
          'The keynote presentation is an impactful, light version of this' +
            ' content that respects all presentation settings from our CMS',
        )}
        enablePresent={(): void => handlePresent()}
        text={t('Present Keynote')}
        cwaAxiosTaskbar={cwaAxiosTaskbar}
        downloadPath={downloadPath && `${downloadPath}/keynote-pptx-async`}
        trackPresentPanelDownload={trackPresentPanelDownload}
      />
      <div className={styles.spacing} />
      <PresentPanelSection
        header={t('Full written')}
        body={t(
          'The full written version keeps all of the content in a' +
            ' presentation-ready layout.',
        )}
        enablePresent={(): void => handlePresent(true)}
        text={t('Present Full Written')}
        cwaAxiosTaskbar={cwaAxiosTaskbar}
        downloadPath={downloadPath && `${downloadPath}/full-written-pptx-async`}
        trackPresentPanelDownload={trackPresentPanelDownload}
      />
    </>
  );
};

const PresentPanel = (
  handlePresent: HandlePresentType,
  cwaAxiosTaskbar: AxiosInstance,
  downloadPath: string | undefined,
  trackPresentPanelDownload: TrackPresentPanelDownloadFn,
): CustomPanelType => {
  const PresentPanelRootComponent = (): JSX.Element => (
    <PresentPanelComponent
      handlePresent={handlePresent}
      cwaAxiosTaskbar={cwaAxiosTaskbar}
      downloadPath={downloadPath}
      trackPresentPanelDownload={trackPresentPanelDownload}
    />
  );
  return {
    bridge: {
      default: createBridgeComponent({
        rootComponent: PresentPanelRootComponent,
      }),
    },
    closeSidebar: noop,
    iconName: 'present2',
    title: <Trans>Present</Trans>,
    subtitle: (
      <Trans>
        Here you can present online or download presentation-ready versions of
        this content piece.
      </Trans>
    ),
    color: 'primary',
    width: 400,
    id: 'present-panel',
  };
};

export default PresentPanel;
